import type { LanguageConfig } from '@/types'

// Japanese language configuration - complete and self-contained
export const japanese: LanguageConfig = {
  code: 'ja',
  name: '日本語',
  dir: 'ltr',
  path: '/ja',  // /ja path for Japanese

  manifest: {
    name: '無料オンラインMP3カッター＆音声トリマー',
    shortName: 'MP3カッター'
  },

  seo: {
    // Meta
    title: '無料オンラインMP3カット・音声トリミング – アップロード不要で100%プライベート | CutMP3',
    description: 'ブラウザ上で直接MP3や音声ファイルを無料でカット。アップロード不要 - ファイルは端末の外に出ません。登録不要、ウォーターマークなし。曲を数秒でトリミングし、着信音も作成できます。',
    keywords: 'MP3カット, 音声カット, 音楽カット, 無料 mp3 カッター, 音声トリミング, 曲をカット, 着信音作成, 着メロ, オンライン音声編集, mp3 切り抜き, 音楽編集 無料, 音声編集 ブラウザ, mp3 トリマー, アップロード不要 音声カット, プライベート 音声編集',

    // Open Graph
    ogLocale: 'ja_JP',
    ogTitle: '無料オンラインMP3カット・音声トリミング – アップロード不要で100%プライベート',
    ogDescription: 'ブラウザ上で直接MP3や音声ファイルを無料でカット。アップロード不要 - ファイルは端末の外に出ません。登録不要、ウォーターマークなし。',
    ogSiteName: 'MP3カッター - 音声トリマー',

    // Twitter
    twitterTitle: '無料オンラインMP3カット・音声トリミング – アップロード不要で100%プライベート',
    twitterDescription: 'ブラウザ上で直接MP3や音声ファイルを無料でカット。アップロード不要 - ファイルは端末の外に出ません。登録不要、ウォーターマークなし。',

    // PWA
    appleWebAppTitle: '音声カッター',

    // JSON-LD Schema
    schemaName: '無料オンラインMP3カッター＆音声トリマー',
    schemaDescription: '無料のオンラインMP3カッター・音声トリマー。曲をオンラインでカットし、音声ファイルをトリミングして着信音を作成。高速・シンプル・完全無料 - アップロード不要でブラウザで動作します。',
    schemaAlternateNames: ['MP3カッター', '音声トリマー', '音楽カッター', '着信音メーカー', 'MP3トリマー', '音声カッター'],
    schemaFeatureList: [
      'MP3カッター - MP3ファイルをオンラインでカット',
      '音声トリマー - あらゆる音声ファイルをトリミング',
      '音楽カット - 曲を精度よくカット',
      '着信音メーカー - オリジナル着信音を作成',
      '音楽編集 - 音楽ファイルの編集とカット',
      '音声カッター - 音声セグメントを切り抜き',
      'MP3トリマー - MP3を簡単にトリミング',
      'オンライン音声エディタ - ブラウザで音声を編集',
      'オンライン音声カット - ダウンロード不要',
      '視覚的な波形編集',
      'MP3・WAVへの高品質エクスポート',
      'インストール不要',
      'プライベート - ファイルはブラウザの外に出ません'
    ],
    schemaCurrency: 'USD',

    // HowTo Schema
    schemaHowToName: '無料で音声をオンラインカットする方法',
    schemaHowToDescription: 'ブラウザベースの音声カッターでMP3・WAVなどの音声ファイルを無料でオンラインカット。アップロード不要 - ファイルは端末上でプライベートに保たれます。',
    schemaHowToSteps: [
      { name: '音声ファイルを選択', text: '「音声ファイルを選択」ボタンをクリックするか、MP3・WAV・OGG・AAC・FLACファイルをアップロードエリアにドラッグ＆ドロップします。サーバーにアップロードされることなく、ブラウザ上で即座に読み込まれます。' },
      { name: '音声をカット・トリミング', text: '視覚的な波形エディタで必要な部分を選択します。開始・終了マーカーをドラッグしてカット位置を決め、再生ボタンでプレビューしながらカットポイントを微調整できます。' },
      { name: 'カットした音声をダウンロード', text: 'エクスポートボタンをクリックし、出力形式（MP3またはWAV）を選んでトリミング済みの音声ファイルをダウンロードします。処理はすべてブラウザ内で行われ、プライバシーと速度を最大化します。' }
    ],

    // Breadcrumb
    schemaBreadcrumbHome: 'ホーム'
  },

  ui: {
    title: '無料オンラインMP3カッター＆音声トリマー',
    subtitle: 'プライベートに曲をカット・音声をトリミング - アップロード不要、登録不要、ウォーターマークなし',

    // Instructions
    instructionsHeading: '3ステップで音声をオンラインカットする方法',
    instructionSelectTitle: '音声ファイルを選択',
    instructionSelectDesc: '上の「音声ファイルを選択」ボタンをクリックしてMP3・WAV・OGG・AAC・FLACファイルを読み込みます。オンラインMP3カッターが主要な音声形式に対応し、曲のカットを簡単にします',
    instructionEditTitle: '音声をカット・トリミング',
    instructionEditDesc: '音声カッターで必要なセクションを選択します。波形上のマーカーを動かして曲を精密にカット。着信音作成や音楽ファイルのトリミングに最適です',
    instructionExportTitle: 'カットした音声をダウンロード',
    instructionExportDesc: 'トリミングした音声をMP3またはWAVで書き出します。着信音作成にも音楽編集にも、高品質な結果をその場でお届けします',

    // Footer
    footerDescription: '無料オンラインMP3カッター・音声トリマー - ブラウザ上で直接曲をカットし、音声ファイルをトリミングして着信音を作成。安全・プライベート・アップロード不要',
    footerKeywords: 'MP3カッター | 音声トリマー | 音楽カット | オンライン音声カット | MP3トリマー | 音楽編集 | 着信音メーカー | オンライン音声エディタ | 音声カッター',
    footerMadeWith: '制作:',
    footerBy: 'by',

    uploadPrompt: '音声ファイルを選択（MP3、WAV、OGG、AAC、FLAC）またはここにドラッグ',
    uploadButton: '音声ファイルを選択',
    dragDropText: 'ここにファイルをドラッグ＆ドロップ',
    supportedFormats: '対応形式: MP3、WAV、OGG、AAC、FLAC、M4A',
    startTime: '開始時間',
    endTime: '終了時間',
    duration: '長さ',
    play: '再生',
    pause: '一時停止',
    stop: '停止',
    replay: '最初から',
    loop: 'ループ',
    export: '書き出し',
    exporting: '書き出し中...',
    format: '形式',
    quality: '品質',
    download: 'ダウンロード',
    reset: '新しいファイル',
    cancel: 'キャンセル',
    error: 'エラー',
    success: '成功',
    loading: '読み込み中...',
    processing: '処理中...',
    invalidFile: '無効なファイルです。音声ファイルを選択してください。',
    fileTooLarge: 'ファイルが大きすぎます。最大サイズは100MBです。',
    unsupportedFormat: '対応していない形式です。MP3、WAV、OGG、AAC、FLACファイルを選択してください。',
    exportSuccess: 'ファイルの書き出しが完了しました！',
    exportError: '書き出し中にエラーが発生しました。もう一度お試しください。',

    // Feedback Form
    contactTitle: 'メッセージを送る',
    contactText: 'ツールを気に入っていただけましたか？ご意見をお聞かせください。不具合の報告や改善のご提案もお待ちしています。',
    contactButton: 'フィードバックを送信',
    contactName: 'お名前 *',
    contactNamePlaceholder: 'お名前を入力してください',
    contactEmail: 'メールアドレス *',
    contactEmailPlaceholder: 'example@email.com',
    contactSubject: '件名',
    contactSubjectPlaceholder: 'メッセージの件名',
    contactMessage: 'メッセージ *',
    contactMessagePlaceholder: 'こちらにメッセージをご記入ください...',
    contactSubmit: 'メッセージを送信',
    contactSubmitting: '送信中...',
    contactCancel: 'キャンセル',
    contactSuccessTitle: 'メッセージを送信しました！',
    contactSuccessText: 'ご連絡ありがとうございます。',
    contactErrorTitle: 'メッセージの送信に失敗しました',
    contactErrorText: 'もう一度お試しいただくか、直接メールでお問い合わせください:',
    contactEmailLink: 'audio.cutter.israel@gmail.com',
    contactRetry: '再試行',
    contactClose: '閉じる',

    // Edit mode
    modeCut: '切り抜き',
    modeTrim: '削除',
    modeCutTooltip: '選択した部分を残し、前後を削除します',
    modeTrimTooltip: '選択した部分を削除し、前後を残します',

    // FAQ
    faqTitle: 'よくある質問',
    faqItems: [
      { question: 'MP3ファイルをオンラインでカットするには？', answer: '「音声ファイルを選択」をクリックしてMP3を読み込み、波形エディタで必要な部分を選び、「書き出し」をクリックしてトリミング済みファイルをダウンロードするだけです。すべての処理はブラウザ内で行われます。' },
      { question: 'このMP3カッターは無料ですか？', answer: 'はい、制限なしで完全無料です。隠れた費用もウォーターマークも登録も不要で、好きなだけファイルをカットできます。' },
      { question: '対応している音声形式は？', answer: 'MP3、WAV、OGG、AAC、FLAC、M4Aファイルに対応しています。カットした音声はMP3またはWAV形式で書き出せます。' },
      { question: '着信音を作成できますか？', answer: 'もちろん！曲を選び、希望の長さ（着信音なら通常30秒）にトリミングしてMP3で書き出すだけです。スマホの着信音としてすぐに使えます。' },
      { question: '安全ですか？ファイルはサーバーにアップロードされますか？', answer: 'ファイルが端末の外に出ることはありません。すべての音声処理はWeb Audio API技術によりブラウザ内で完結します。ファイルは一切サーバーにアップロードされず、完全なプライバシーが守られます。' },
      { question: '最大ファイルサイズは？', answer: '最大100MBまでの音声ファイルを処理できます。処理はすべてブラウザ内でローカルに行われるため、大きなファイルは端末の性能によって読み込みに時間がかかる場合があります。' },
      { question: '音声ファイルの真ん中の部分を削除するには？', answer: '削除モードを使います（波形の上にある「削除」ボタンをクリック）。取り除きたいセクションを選択すると、その前後の部分が残り、真ん中の部分が削除されます。' }
    ],
    relatedToolsTitle: '関連ツール',

    // Privacy trust badges
    trustItems: [
      { title: 'アップロード不要 - 100%ブラウザ内で処理', text: '音声はWeb Audio技術で端末上で処理されます。ファイルがサーバーに届くことはありません。' },
      { title: '無料・登録不要・ウォーターマークなし', text: 'カット回数無制限、フル品質の書き出し。アカウント登録も隠れた費用もなし - 永久に無料です。' },
      { title: 'インストール不要 - どんな端末でも動作', text: 'スマホ、タブレット、パソコン - アプリ不要でブラウザから直接音声をカットできます。' }
    ],

    // Accessibility
    skipToContent: '本文へスキップ',
    required: '必須',
  },

  formatLabels: {
    mp3: 'MP3',
    wav: 'WAV',
    ogg: 'OGG',
    aac: 'AAC'
  },

  qualityLabels: {
    low: '低 (128 kbps)',
    medium: '中 (192 kbps)',
    high: '高 (256 kbps)',
    best: '最高 (320 kbps)'
  },

  spokes: {
    'mp3-wav-converter': {
      slug: 'mp3-wav-converter',
      ui: {
        title: 'MP3→WAV変換（WAV→MP3にも対応）',
        subtitle: '音声ファイルをブラウザ上で直接MP3やWAVに変換 - 無料・アップロード不要',
        heroText: 'MP3とWAVを数秒で相互変換。MP3・WAV・OGG・AAC・FLAC・M4Aファイルを読み込み、MP3（最大320 kbps）または非圧縮WAVとして書き出せます - インストール不要で、ファイルが端末の外に出ることもありません。トリミングも編集も不要 - ファイルを読み込み、出力形式を選んでダウンロードするだけです。',
        faqItems: [
          { question: 'MP3をWAVに変換するには？', answer: 'MP3ファイルを読み込み、「書き出し」をクリックしてWAVを出力形式として選択します。アップロードなしでデコードされ、非圧縮WAV音声として保存されます - すべてブラウザ内で完結します。' },
          { question: 'WAVをMP3に変換するには？', answer: 'WAVファイルを読み込み、「書き出し」でMP3を選び、ビットレート（128〜320 kbps）を指定します。320 kbpsならほとんどのリスナーはオリジナルとの違いを判別できません。' },
          { question: 'どの形式から変換できますか？', answer: 'MP3、WAV、OGG、AAC、FLAC、M4Aファイルを読み込めます。出力はMP3またはWAVなので、これらの形式をどちらにも変換できます。' },
          { question: 'MP3とWAV、どちらを選ぶべき？', answer: '最高品質やその後の編集にはWAV（非圧縮）を。共有や持ち運びに便利な小さいファイルにはMP3を。スマホやメッセージアプリなど、どこでも再生できます。' },
          { question: '音声はサーバーにアップロードされますか？', answer: 'いいえ。変換はJavaScriptにより完全にブラウザ内で動作します。ファイルは端末の外に出ないため、長い録音でも速くプライベートに変換できます。' },
          { question: 'ファイルの一部だけをカットできますか？', answer: 'このページはファイル全体を変換します。曲や録音の一部だけを切り出したい場合は、トップページの音声カッターをご利用ください - 同じ形式に対応し、MP3とWAVの書き出しにも対応しています。' }
        ],
        instructionsHeading: '3ステップで音声をオンライン変換する方法',
        instructionSelectTitle: 'ファイルを読み込む',
        instructionSelectDesc: '「音声ファイルを選択」ボタンをクリックするか、MP3・WAV・OGG・AAC・FLAC・M4Aファイルをドラッグ＆ドロップします。ブラウザ上で即座に読み込まれ、サーバーには何もアップロードされません。',
        instructionEditTitle: '出力形式を選ぶ',
        instructionEditDesc: 'MP3（ビットレートは最大320 kbpsまで選択可）または非圧縮WAVを選びます。編集は不要 - ファイル全体が変換されます。',
        instructionExportTitle: 'ファイルをダウンロード',
        instructionExportDesc: '「書き出し」をクリックして変換済みの音声ファイルをダウンロードします。変換はすべて端末上で行われ、プライバシーと速度を最大化します。'
      },
      seo: {
        title: 'MP3→WAV変換 – WAV→MP3も無料オンライン | CutMP3',
        description: 'ブラウザ上でMP3をWAVに、WAVをMP3に無料変換。OGG・AAC・FLAC・M4Aにも対応。アップロード不要、登録不要、インストール不要 - ファイルは端末の外に出ません。',
        keywords: 'MP3 WAV 変換, WAV MP3 変換, mp3 から wav に変換, wav から mp3 に変換, 無料 音声変換, 音声形式変換 オンライン, ogg 変換, m4a wav 変換, ブラウザ 音声コンバーター',
        ogTitle: 'MP3→WAV変換 – WAV→MP3も無料オンライン',
        ogDescription: 'ブラウザ上でMP3とWAVを相互変換。アップロード不要、インストール不要 - 100%プライベート。',
        twitterTitle: 'MP3→WAV変換 – WAV→MP3も無料オンライン',
        twitterDescription: 'ブラウザ上でMP3とWAVを相互変換。アップロード不要、インストール不要。',
        schemaName: 'MP3→WAVコンバーター',
        schemaDescription: 'MP3とWAV（OGG・AAC・FLAC・M4Aも入力可）を無料で相互変換するオンラインツール。完全にブラウザ内で動作 - ファイルは端末の外に出ません。',
        schemaFeatureList: [
          'MP3をWAVに変換',
          'WAVをMP3に変換（最大320 kbps）',
          'OGG・AAC・FLAC・M4Aの入力に対応',
          '品質とサンプルレートを選択可能',
          'アップロード不要 - 変換はブラウザ内で実行',
          '無料 - 登録もインストールも不要'
        ],
        schemaHowToName: 'MP3をオンラインでWAVに変換する方法',
        schemaHowToDescription: 'サーバーにファイルをアップロードせずに、ブラウザ上でMP3をWAV（またはWAVをMP3）へ無料変換します。',
        schemaHowToSteps: [
          { name: 'ファイルを読み込む', text: '「音声ファイルを選択」をクリックするか、MP3・WAV・OGG・AAC・FLAC・M4Aファイルをドラッグ＆ドロップします。ブラウザ上で即座にデコードされます。' },
          { name: '出力形式を選ぶ', text: '「書き出し」をクリックし、MP3（好みのビットレート）または非圧縮WAVを選択します。' },
          { name: '変換したファイルをダウンロード', text: '「ダウンロード」をクリックして変換済みの音声を保存します。変換はすべて端末上でローカルに実行されます。' }
        ]
      }
    },
    'make-iphone-ringtone': {
      slug: 'make-iphone-ringtone',
      ui: {
        title: '無料iPhone着信音メーカー',
        subtitle: 'あらゆる音声ファイルからオリジナルM4R着信音を作成 - 40秒以内',
        heroText: 'MP3、WAV、その他の音声ファイルをオリジナルのiPhone着信音に変えましょう。無料のオンラインツールで、最高の30秒クリップを選び、精密にトリミングして、iPhoneですぐ使える状態でダウンロードできます。ソフトウェアのインストールは不要です。',
        faqItems: [
          { question: 'iPhoneの着信音を作るには？', answer: '音声ファイルを読み込み、波形エディタで30秒以内のクリップを選び、MP3で書き出します。その後、iTunes/Finderまたはファイル管理アプリでiPhoneに追加してください。' },
          { question: 'iPhone着信音の最大の長さは？', answer: 'iPhoneの着信音は最大40秒までですが、30秒が最も一般的です。トリマーで音声を理想の長さにカットしましょう。' },
          { question: 'iPhone着信音にはどんな形式が必要？', answer: 'iPhoneの着信音はM4R形式ですが、どの音声形式からでも始められます。当ツールでMP3として書き出し、拡張子を .m4r に変更するか、iTunesで変換してください。' },
          { question: 'YouTube動画から着信音を作れますか？', answer: 'まず別のツールで動画から音声をダウンロードする必要があります。音声ファイル（MP3、WAVなど）が手元にあれば、ここに読み込んで着信音にトリミングできます。' },
          { question: 'この着信音メーカーは完全に無料ですか？', answer: 'はい、制限なしで100%無料です。着信音は何個でも作成でき、登録不要、ファイルはブラウザの外に出ないのでプライバシーも完全に守られます。' }
        ]
      },
      seo: {
        title: '無料iPhone着信音メーカー – オンラインで着信音作成 | CutMP3',
        description: 'あらゆる音声ファイルから無料でオリジナルiPhone着信音を作成。30秒にトリミングしてダウンロード - アップロード不要、音楽は端末の外に出ません。ブラウザで動作。',
        keywords: 'iphone 着信音 作成, 着信音 作成 無料, 着信音 作り方, オンライン 着信音, 着信音 メーカー, mp3 着信音 変換, 着信音 カット, 曲から着信音, カスタム着信音',
        ogTitle: '無料iPhone着信音メーカー – オンラインで着信音作成',
        ogDescription: 'あらゆる音声ファイルから無料でオリジナルiPhone着信音を作成。読み込み、30秒にトリミング、ダウンロード。インストール不要。',
        twitterTitle: '無料iPhone着信音メーカー – オンラインで着信音作成',
        twitterDescription: 'あらゆる音声ファイルから無料でオリジナルiPhone着信音を作成。読み込み、トリミング、ダウンロード。ブラウザで動作。',
        schemaName: '無料iPhone着信音メーカー',
        schemaDescription: 'あらゆる音声ファイルからオリジナルiPhone着信音を作成。MP3・WAVなどの音声を読み込み、理想の長さにトリミングして、着信音を無料でダウンロード。',
        schemaFeatureList: [
          'あらゆる音声ファイルからiPhone着信音を作成',
          '音声を30秒の着信音クリップにトリミング',
          '精密カットのための視覚的波形エディタ',
          'MP3・WAV・OGG・AAC・FLACに対応',
          '無料 - 登録もインストールも不要',
          'プライベート - 音声はブラウザの外に出ません'
        ],
        schemaHowToName: 'iPhone着信音をオンラインで作る方法',
        schemaHowToDescription: '音声ファイルを読み込み、30秒にトリミングして、オリジナルのiPhone着信音を作成します。',
        schemaHowToSteps: [
          { name: '音声を読み込む', text: '「音声ファイルを選択」をクリックするか、MP3・WAVなどの音声ファイルをドラッグ＆ドロップします。ブラウザ上で即座に読み込まれます。' },
          { name: '着信音の長さにトリミング', text: '波形エディタで30秒のクリップを選択します。マーカーをドラッグして、着信音にぴったりの曲の一番良い部分を選びましょう。' },
          { name: '着信音をダウンロード', text: '「書き出し」をクリックし、MP3形式を選んでダウンロード。iTunes、Finder、またはファイル管理アプリでiPhoneに転送してください。' }
        ]
      }
    },
    'cut-audio-for-instagram': {
      slug: 'cut-audio-for-instagram',
      ui: {
        title: 'Instagramリール＆ストーリーズ用の音声カット',
        subtitle: 'Instagram・TikTok・SNS向けの音声クリップをトリミング - 毎回ぴったりの長さに',
        heroText: 'Instagramリールやストーリーズにぴったりの音声クリップが必要ですか？無料オンラインツールで、曲や音声ファイルをSNSに最適な長さに正確にカットできます。トリミング、プレビュー、ダウンロードが数秒で完了します。',
        faqItems: [
          { question: 'Instagramリールに最適な音声の長さは？', answer: 'Instagramリールは最大90秒です。エンゲージメントを最大化するには15〜30秒が理想的です。当エディタで音声をぴったりの長さにトリミングしましょう。' },
          { question: 'Instagramストーリーズ用の音声もカットできますか？', answer: 'はい！Instagramストーリーズは1スライド15秒です。音声クリップをちょうど15秒（またはその倍数）にトリミングすれば、シームレスなストーリーズBGMになります。' },
          { question: 'Instagramが対応する音声形式は？', answer: 'InstagramはMP3とAACの音声に対応しています。当ツールでトリミング済み音声をMP3として書き出せば、リールやストーリーズですぐに使えます。' },
          { question: 'TikTokの音声にも使えますか？', answer: 'もちろん！当音声カッターはあらゆるSNSに使えます。音声を希望の長さにカットして、TikTok、Instagram、YouTube Shortsなどどこでもご利用ください。' },
          { question: 'カットした音声をInstagramリールに追加するには？', answer: 'トリミング済み音声をダウンロードしたら、Instagramで新しいリールを作成し、「音声を追加」オプションを使って端末からオリジナルクリップをアップロードします。' }
        ]
      },
      seo: {
        title: 'Instagram用音声カット – リール＆ストーリーズ音声トリミング | CutMP3',
        description: 'Instagram用の音声を無料でカット。リール（15〜90秒）、ストーリーズ（15秒）、TikTok向けにブラウザ上でトリミング - アップロード不要、登録不要、ウォーターマークなし。',
        keywords: 'instagram 音声 カット, リール 音声 トリミング, インスタ リール 音声, instagram 音声 編集, tiktok 音声 カット, sns 音声 編集, インスタ 音声 加工, リール 音楽 カット',
        ogTitle: 'Instagramリール＆ストーリーズ用音声カット - 無料オンラインツール',
        ogDescription: 'Instagramリールやストーリーズ向けの音声をトリミング。曲をSNSに最適な長さにカット。無料・高速。',
        twitterTitle: 'Instagramリール＆ストーリーズ用音声カット - 無料オンラインツール',
        twitterDescription: 'Instagramリールやストーリーズ向けの音声をトリミング。曲をSNSに最適な長さにカット。無料・高速。',
        schemaName: 'Instagram用音声カット - SNS音声トリマー',
        schemaDescription: 'Instagramリール、ストーリーズ、TikTokなどSNS向けの音声をトリミングする無料オンラインツール。曲をぴったりの長さにカットします。',
        schemaFeatureList: [
          'Instagramリール向けの音声トリミング（最大90秒）',
          'Instagramストーリーズ向けの音声カット（15秒）',
          'TikTokやYouTube Shortsにも最適',
          '精密トリミングのための視覚的波形エディタ',
          'SNSですぐ使えるMP3で書き出し',
          '無料 - 登録もインストールも不要'
        ],
        schemaHowToName: 'Instagramリール用に音声をカットする方法',
        schemaHowToDescription: '音声ファイルをInstagramリール、ストーリーズなどSNSに最適な長さにトリミングします。',
        schemaHowToSteps: [
          { name: '音声を読み込む', text: '「音声ファイルを選択」をクリックするか、曲や音声クリップをドラッグ＆ドロップします。MP3、WAVなどの形式に対応しています。' },
          { name: '適切な長さにトリミング', text: '波形エディタでぴったりのクリップを選びます。リールは15〜30秒、ストーリーズはちょうど15秒が目安です。' },
          { name: 'SNS用にダウンロード', text: '「書き出し」をクリックし、MP3を選んでダウンロード。トリミング済み音声をInstagram、TikTokなどに直接アップロードできます。' }
        ]
      }
    },
    'trim-voice-recording': {
      slug: 'trim-voice-recording',
      ui: {
        title: 'ボイス録音をオンラインでトリミング',
        subtitle: 'ボイスメモや録音をクリーンアップ - 無音や不要な部分を削除',
        heroText: 'ボイスメモ、インタビュー、音声メモを素早くトリミング。無料の視覚的音声エディタで、気まずい無音、言い直し、不要なセクションを削除できます。サーバーへのアップロードは一切ありません。',
        faqItems: [
          { question: 'ボイスメモをトリミングするには？', answer: 'ボイスメモ（MP3、M4A、WAVなど）を読み込み、波形表示で残したい部分を視覚的に確認して選択し、書き出すだけです。とても簡単です。' },
          { question: '録音から無音を削除できますか？', answer: 'はい！波形を見れば無音セクション（平坦な部分）が一目でわかります。切り抜きモードで残したい部分を選択するか、不要な無音部分を選んで削除モードで取り除きます。' },
          { question: '対応している録音形式は？', answer: '一般的な録音形式すべてに対応しています: MP3、M4A（iPhoneボイスメモ）、WAV、OGG、AAC、FLAC。MP3またはWAVで書き出せます。' },
          { question: 'ポッドキャスト音声のトリミングにも使えますか？', answer: 'はい！ポッドキャスト録音を読み込み、波形エディタでイントロ、アウトロ、ミス、不要なセクションをカットできます。手軽なポッドキャスト編集に最適です。' },
          { question: '録音はプライベートに保たれますか？', answer: 'もちろんです。録音は完全にブラウザ内で処理されます。サーバーには何もアップロードされず、音声は端末上で完全にプライベートに保たれます。' }
        ]
      },
      seo: {
        title: 'ボイス録音の無料トリミング – ボイスメモをカット | CutMP3',
        description: 'ボイス録音やボイスメモをオンラインで無料トリミング。無音や不要部分を削除。プライベート - ファイルはブラウザの外に出ません。',
        keywords: 'ボイスメモ トリミング, 録音 カット, 録音 編集 オンライン, ボイス録音 編集, 音声メモ カット, 録音 加工, ボイスメモ 編集 無料, m4a 録音 トリミング',
        ogTitle: 'ボイス録音の無料トリミング – ボイスメモをカット',
        ogDescription: 'ボイス録音やボイスメモをオンラインで無料トリミング。無音や不要部分を削除。プライベート - ファイルはブラウザの外に出ません。',
        twitterTitle: 'ボイス録音の無料トリミング – ボイスメモをカット',
        twitterDescription: 'ボイス録音をオンラインで無料トリミング。無音や不要部分を削除。プライベート・高速。',
        schemaName: 'ボイス録音オンライントリマー',
        schemaDescription: 'ボイス録音、ボイスメモ、ポッドキャスト音声をトリミングする無料オンラインツール。視覚的エディタで無音や不要セクションを削除します。',
        schemaFeatureList: [
          'ボイスメモや録音をトリミング',
          '音声から無音を削除',
          'ポッドキャスト録音を編集',
          'M4A・MP3・WAVなどに対応',
          '精密編集のための視覚的波形',
          'プライベート - 録音はブラウザの外に出ません'
        ],
        schemaHowToName: 'ボイス録音をオンラインでトリミングする方法',
        schemaHowToDescription: '無料のブラウザベースエディタで不要セクションをトリミングし、ボイスメモ、ポッドキャスト録音、音声メモをクリーンアップします。',
        schemaHowToSteps: [
          { name: '録音を読み込む', text: '「音声ファイルを選択」をクリックするか、ボイスメモ、ポッドキャスト、録音をドラッグ＆ドロップします。M4A、MP3、WAVなどの形式に対応しています。' },
          { name: '不要なセクションをトリミング', text: '波形エディタで無音、言い直し、不要な部分を見つけて削除します。切り抜きモードでセクションを残すか、削除モードで取り除きます。' },
          { name: 'トリミング済み録音をダウンロード', text: '「書き出し」をクリックし、形式を選んでクリーンアップ済みの録音をダウンロード。共有や公開、保存ですぐに使えます。' }
        ]
      }
    },
    'wedding-first-dance-song': {
      slug: 'wedding-first-dance-song',
      ui: {
        title: 'ファーストダンスの曲をカット',
        subtitle: '結婚式のファーストダンス、入場、パーティー用に曲を好きな長さに - 無料・プライベート',
        heroText: 'ファーストダンスを理想の長さに。結婚式の曲を読み込み、お気に入りのAメロやサビを選び、ループ再生でプレビューして、DJに渡すクリーンなカットをダウンロード。すべてブラウザ内で完結 - 曲はプライベートに保たれます。',
        faqItems: [
          { question: 'ファーストダンスの曲はどのくらいの長さがいい？', answer: '多くのカップルは60〜120秒を目安にしています - 瞬間を楽しむのに十分で、ゲストの集中が続く長さです。フルサイズの3〜4分の曲はダンスフロアでは長く感じやすいため、良いサビとAメロにカットするのが人気です。' },
          { question: '曲を途中で切れたように聞こえないようにカットするには？', answer: '音楽のフレーズの区切りでカットします: 選択範囲の開始をAメロやサビの直前に置き、フレーズが解決するところで終えます。ループボタンで選択範囲を繰り返しプレビューし、カットが自然に聞こえるまでマーカーを微調整してください。' },
          { question: '入場曲やパーティー用の曲もカットできますか？', answer: 'もちろん。同じツールで入場曲、式への行進の音楽、ケーターカットの曲、パーティープレイリストのイントロを、結婚式のスケジュールに必要な長さにトリミングできます。' },
          { question: '結婚式の曲はプライベートに保たれますか？', answer: 'はい。曲が端末の外に出ることはありません - カットはすべてブラウザ内で行われ、サーバーへのアップロードはありません。MP3またはWAVでカット済みファイルをダウンロードし、USBメモリやメールでDJにお渡しください。' }
        ]
      },
      seo: {
        title: 'ファーストダンスの曲をカット – 結婚式ソングカッター | CutMP3',
        description: 'ファーストダンスの曲を無料で理想の長さにカット。結婚式の入場曲やパーティー楽曲をブラウザ上でトリミング - アップロード不要、曲はプライベートに保たれます。',
        keywords: 'ファーストダンス 曲 カット, 結婚式 曲 短縮, 結婚式 ソングカッター, 入場曲 トリミング, 結婚式 音楽 編集, 披露宴 曲 カット, 結婚式 bgm カット, ファーストダンス 曲 長さ',
        ogTitle: 'ファーストダンスの曲をカット – 結婚式ソングカッター',
        ogDescription: 'ファーストダンスの曲を無料で理想の長さにカット。結婚式の音楽をブラウザ上でトリミング - アップロード不要、曲はプライベートに保たれます。',
        twitterTitle: 'ファーストダンスの曲をカット – 結婚式ソングカッター',
        twitterDescription: 'ファーストダンスの曲を無料で理想の長さにカット。アップロード不要 - 曲はプライベートに保たれます。',
        schemaName: '結婚式ファーストダンス ソングカッター',
        schemaDescription: '結婚式のファーストダンス、入場、パーティープレイリスト向けに曲をカット・短縮する無料オンラインツール。プライベート - 音声はブラウザの外に出ません。',
        schemaFeatureList: [
          'ファーストダンス向けに曲を短縮',
          '結婚式の入場・行進の音楽をトリミング',
          'ループプレビューでカットを完璧に',
          '精密カットのための視覚的波形エディタ',
          'DJ用にMP3またはWAVで書き出し',
          'プライベート - 曲はブラウザの外に出ません'
        ],
        schemaHowToName: 'ファーストダンスの曲をカットする方法',
        schemaHowToDescription: '無料のブラウザベース音声カッターで、曲をファーストダンスにぴったりの長さに短縮します。',
        schemaHowToSteps: [
          { name: '結婚式の曲を読み込む', text: '「音声ファイルを選択」をクリックするか、曲をドラッグ＆ドロップします。どこにもアップロードされず、ブラウザ上で即座に読み込まれます。' },
          { name: 'お気に入りの部分を選択', text: 'マーカーをドラッグして残したいAメロとサビを選びます。通常は60〜120秒です。カットが自然に聞こえるまで、ループボタンでプレビューしましょう。' },
          { name: 'DJ用に書き出す', text: '「書き出し」をクリックしてMP3またはWAVでダウンロード。DJやウェディングプランナーに渡せば、すぐに再生できます。' }
        ]
      }
    },
    'whatsapp-audio-cutter': {
      slug: 'whatsapp-audio-cutter',
      presetSelectionSeconds: 30,
      ui: {
        title: 'WhatsApp音声カッター',
        subtitle: '曲やボイスメモをWhatsAppステータス向け30秒にカット - 無料・アップロード不要',
        heroText: 'WhatsAppステータスにぴったりの30秒クリップをカットしましょう。曲やボイスメモを読み込むと、最初の30秒が自動で選択されます - 選択範囲を一番良い部分までドラッグして、プレビュー、ダウンロードするだけです。アプリ不要、アップロード不要、完全無料。',
        faqItems: [
          { question: 'なぜ30秒なの？', answer: 'WhatsAppステータスの音声・動画クリップは30秒までに制限されています。ここでファイルを読み込むと、ツールが自動で30秒のクリップを選択します - 選択範囲を波形上でドラッグして、好きな部分に移動してください。' },
          { question: 'カットした曲をWhatsAppステータスに載せるには？', answer: '30秒クリップをMP3で書き出し、WhatsAppを開いてステータスに移動し、写真や動画に音声を添付するか、ステータス作成アプリを使います。クリップはステータスの制限にぴったり収まります。' },
          { question: 'WhatsApp用にボイスメモをカットできますか？', answer: 'はい。ボイスメモ（M4A、対応形式で書き出されたOPUS、MP3など）を読み込み、必要な部分をトリミングして書き出します。長い音声メッセージを転送前に短くするのに最適です。' },
          { question: '音声はどこかにアップロードされますか？', answer: 'いいえ。すべてブラウザ内で処理されます - 曲やボイスメモがサーバーに届くことはありません。プライベートで高速、低速回線でも動作します。' }
        ]
      },
      seo: {
        title: 'WhatsApp音声カッター – 30秒ステータス音声をカット | CutMP3',
        description: 'WhatsAppステータス用の音声を無料でカット。曲やボイスメモを30秒制限に収まるようブラウザ上でトリミング - アップロード不要、アプリ不要、ウォーターマークなし。',
        keywords: 'whatsapp 音声 カット, whatsapp ステータス 音声 30秒, ステータス 曲 カット, whatsapp 音声 トリミング, 30秒 音声カット, ボイスメモ whatsapp カット, whatsapp ステータス ソングカッター',
        ogTitle: 'WhatsApp音声カッター – 30秒ステータス音声をカット',
        ogDescription: 'WhatsAppステータス用の音声を無料でカット。曲を30秒制限に収まるようブラウザ上でトリミング - アップロード不要、アプリ不要。',
        twitterTitle: 'WhatsApp音声カッター – 30秒ステータス音声をカット',
        twitterDescription: 'WhatsAppステータス用の音声を無料でカット。曲を30秒にブラウザ上でトリミング - アップロード不要。',
        schemaName: 'WhatsApp音声カッター',
        schemaDescription: '曲やボイスメモをWhatsAppステータス向け30秒にカットする無料オンラインツール。プライベート - 音声はブラウザの外に出ません。',
        schemaFeatureList: [
          'WhatsAppステータスの30秒制限に音声をカット',
          '自動30秒プリセレクション',
          '曲やボイスメモをトリミング',
          '精密カットのための視覚的波形エディタ',
          '共有準備完了のMP3で書き出し',
          'プライベート - 音声はブラウザの外に出ません'
        ],
        schemaHowToName: 'WhatsAppステータス用に音声をカットする方法',
        schemaHowToDescription: '無料のブラウザベース音声カッターで、曲やボイスメモをWhatsAppステータスの30秒制限にトリミングします。',
        schemaHowToSteps: [
          { name: '音声を読み込む', text: '「音声ファイルを選択」をクリックするか、曲やボイスメモをドラッグ＆ドロップします。ツールが自動で30秒のクリップを選択します。' },
          { name: '最高の30秒を選ぶ', text: '選択範囲を波形上でドラッグして、サビ、決めゼリフ、ハイライトなど、好きな部分に移動します。再生ボタンでプレビューしましょう。' },
          { name: '書き出して共有', text: '「書き出し」をクリックしてMP3でダウンロードし、WhatsAppステータスに添付するか、チャットで送信します。' }
        ]
      }
    },
    'podcast-trimmer': {
      slug: 'podcast-trimmer',
      ui: {
        title: 'ポッドキャストトリマー',
        subtitle: 'ポッドキャスト音声をオンラインでトリミング - イントロ、アウトロ、ミスをカット、アップロード不要',
        heroText: 'エディタをインストールせずにポッドキャストのエピソードを手早くクリーンアップ。失敗したイントロのカット、無音のトリミング、SNS向けハイライトクリップの切り出し - すべて視覚的な波形上で、ブラウザ内で完結します。未公開エピソードが端末の外に出ることはありません。',
        faqItems: [
          { question: 'ポッドキャストのエピソードをトリミングするには？', answer: 'エピソードを読み込んでモードを選びます: 切り抜きモードは選択範囲を残し（ハイライトクリップの切り出しに最適）、削除モードは選択範囲を取り除いて前後を残します（ミスや中盤の無音の削除に最適）。' },
          { question: 'エピソードの最大の長さは？', answer: '最大100MBのファイルに対応 - 一般的なポッドキャストのビットレートで約1.5〜2時間のMP3です。処理はすべて端末上でローカルに行われるため、長いエピソードは読み込みに時間がかかる場合があります。' },
          { question: 'ポッドキャスト配信プラットフォームにはどの書き出し品質を使うべき？', answer: 'このツールは192 kbpsのMP3で書き出します。主要ポッドキャストホストの品質推奨を上回っています。さらに編集する予定があれば、ロスレスWAVでの書き出しも可能です。' },
          { question: '未公開エピソードはプライベートに保たれますか？', answer: '完全に保たれます。エピソードは完全にブラウザ内で処理されます - サーバーには何もアップロードされないため、未公開のインタビューや下書きも端末上にとどまります。' }
        ]
      },
      seo: {
        title: 'ポッドキャストトリマー – ポッドキャスト音声を無料トリミング | CutMP3',
        description: 'ポッドキャスト音声をオンラインで無料トリミング。視覚的な波形でイントロ、アウトロ、無音、ミスをカット - エピソードはブラウザの外に出ません、登録不要。',
        keywords: 'ポッドキャスト トリミング, ポッドキャスト 編集, ポッドキャスト 音声 カット, ポッドキャスト エディタ オンライン, 無音 削除 ポッドキャスト, ポッドキャスト イントロ トリミング, ポッドキャスト クリップ 切り抜き',
        ogTitle: 'ポッドキャストトリマー – ポッドキャスト音声を無料トリミング',
        ogDescription: 'ポッドキャスト音声をオンラインで無料トリミング。イントロ、アウトロ、ミスをカット - エピソードはブラウザの外に出ません。',
        twitterTitle: 'ポッドキャストトリマー – ポッドキャスト音声を無料トリミング',
        twitterDescription: 'ポッドキャスト音声をオンラインで無料トリミング。エピソードはブラウザの外に出ません - アップロード不要、登録不要。',
        schemaName: 'ポッドキャストトリマー',
        schemaDescription: 'ポッドキャストのエピソードをトリミングする無料オンラインツール: イントロ・アウトロのカット、無音やミスの削除、ハイライトクリップの切り出し。プライベート - 音声はブラウザの外に出ません。',
        schemaFeatureList: [
          'ポッドキャストのイントロ・アウトロをトリミング',
          '削除モードでミスや無音を除去',
          '切り抜きモードでハイライトクリップを切り出し',
          '最大100MBのエピソードに対応',
          'MP3（192 kbps）またはロスレスWAVで書き出し',
          'プライベート - エピソードはブラウザの外に出ません'
        ],
        schemaHowToName: 'ポッドキャストのエピソードをオンラインでトリミングする方法',
        schemaHowToDescription: '無料のブラウザベーストリマーで、ポッドキャストのエピソードからイントロ、アウトロ、ミス、無音をカットします。',
        schemaHowToSteps: [
          { name: 'エピソードを読み込む', text: '「音声ファイルを選択」をクリックするか、エピソード（MP3、WAV、M4Aなど）をドラッグ＆ドロップします。ローカルで読み込まれ、アップロードはされません。' },
          { name: '波形上でカット・トリミング', text: '削除モードで中盤のミスや無音を取り除くか、切り抜きモードでクリップを切り出します。再生ボタンで編集結果をプレビューしましょう。' },
          { name: '結果を書き出す', text: '「書き出し」をクリックしてMP3（192 kbps）またはWAVでダウンロード。公開やSNSクリップとしての共有にすぐ使えます。' }
        ]
      }
    },
    'alarm-sound-maker': {
      slug: 'alarm-sound-maker',
      presetSelectionSeconds: 30,
      ui: {
        title: 'アラームサウンドメーカー',
        subtitle: '好きな曲をオリジナルアラーム音に - ぴったりの30秒をカット、無料',
        heroText: '本当に好きな音楽で目覚めましょう。好きな曲を読み込み、エネルギッシュな30秒クリップをカット - ツールが自動で選択します - AndroidやiPhoneのアラーム音や通知音に設定できます。無料、アプリ不要、アップロード不要。',
        faqItems: [
          { question: 'Androidでオリジナルのアラーム音を設定するには？', answer: 'クリップをMP3で書き出してスマホに保存します。時計アプリを開き、アラームを編集して音を選択し、「新規追加」を選ぶか端末のファイルからカットしたクリップを選んでください。' },
          { question: 'iPhoneでオリジナルのアラーム音を設定するには？', answer: 'iPhoneではアラーム音は着信音として追加する必要があります。クリップをMP3で書き出し、iPhoneのGarageBand（またはパソコンのiTunes/Finder）で着信音に変換し、時計 > アラーム > サウンドで選択します。' },
          { question: '良いアラーム音の条件は？', answer: 'エネルギッシュな始まりの20〜30秒クリップが最適です - 静かなイントロではなくサビや盛り上がりを選べば、アラームが1秒目から注意を引きます。ツールは30秒を自動選択します。最もエネルギッシュな部分までドラッグしてください。' },
          { question: '曲はどこかにアップロードされますか？', answer: 'いいえ。曲は完全にブラウザ内で処理されます - サーバーには何も届きません。アラーム音や通知音は好きなだけ、無料で作成できます。' }
        ]
      },
      seo: {
        title: 'アラームサウンドメーカー – オリジナルアラーム音を無料作成 | CutMP3',
        description: '好きな曲から無料でオリジナルアラーム音を作成。ブラウザ上で30秒クリップをカットし、AndroidやiPhoneのアラームに設定。アップロード不要、アプリ不要。',
        keywords: 'アラーム音 作成, オリジナル アラーム音, 曲から アラーム音, アラーム音 カット, アラーム サウンド 無料, mp3 アラーム音, 通知音 作成, カスタム 通知音, 目覚まし 音 作成',
        ogTitle: 'アラームサウンドメーカー – オリジナルアラーム音を無料作成',
        ogDescription: '好きな曲から無料でオリジナルアラーム音を作成。ブラウザ上で30秒クリップをカット - アップロード不要、アプリ不要。',
        twitterTitle: 'アラームサウンドメーカー – オリジナルアラーム音を無料作成',
        twitterDescription: '好きな曲から無料でオリジナルアラーム音を作成。ブラウザ上で30秒クリップをカット。',
        schemaName: 'アラームサウンドメーカー',
        schemaDescription: '好きな曲からオリジナルのアラーム音・通知音を作成する無料オンラインツール。ブラウザ上で30秒クリップをカット - プライベート、アップロード不要。',
        schemaFeatureList: [
          '好きな曲からオリジナルのアラーム音を作成',
          '自動30秒プリセレクション',
          '通知音の作成にも対応',
          'AndroidとiPhoneの両方で動作',
          'アラームにすぐ設定できるMP3で書き出し',
          'プライベート - 音楽はブラウザの外に出ません'
        ],
        schemaHowToName: 'オリジナルのアラーム音を作る方法',
        schemaHowToDescription: '無料のブラウザベース音声カッターで、好きな曲をオリジナルのアラーム音・通知音に変えます。',
        schemaHowToSteps: [
          { name: '曲を読み込む', text: '「音声ファイルを選択」をクリックするか、目覚ましに使いたい曲をドラッグ＆ドロップします。30秒クリップが自動で選択されます。' },
          { name: 'エネルギッシュな30秒を選ぶ', text: '選択範囲をサビや盛り上がりまでドラッグすれば、アラームが力強く始まります。再生ボタンでプレビューしましょう。' },
          { name: '書き出してアラームに設定', text: 'MP3でダウンロードし、Androidなら時計アプリで設定、iPhoneなら着信音に変換してアラーム音にします。' }
        ]
      }
    }
  },

  spokeLinks: [
    { slug: 'mp3-wav-converter', label: 'MP3→WAVコンバーター', description: '音声を無料でMP3やWAVに変換' },
    { slug: 'make-iphone-ringtone', label: 'iPhone着信音メーカー', description: '好きな曲からオリジナル着信音を作成' },
    { slug: 'cut-audio-for-instagram', label: 'Instagram用音声', description: 'リールやストーリーズ向けの音声クリップをトリミング' },
    { slug: 'trim-voice-recording', label: 'ボイス録音のトリミング', description: 'ボイスメモや録音をクリーンアップ' },
    { slug: 'wedding-first-dance-song', label: 'ファーストダンス曲カッター', description: '結婚式の曲を理想の長さにカット' },
    { slug: 'whatsapp-audio-cutter', label: 'WhatsApp音声カッター', description: 'WhatsAppステータス用の30秒クリップをカット' },
    { slug: 'podcast-trimmer', label: 'ポッドキャストトリマー', description: 'イントロ、アウトロ、ミスをトリミング' },
    { slug: 'alarm-sound-maker', label: 'アラームサウンドメーカー', description: '好きな曲からオリジナルアラーム音を作成' }
  ]
}